import { Dialog, DialogPanel } from '@headlessui/react'
import { ChevronRightIcon, XMarkIcon } from '@heroicons/react/20/solid'
import {
	Bars3Icon,
	BellIcon,
	ClipboardIcon,
	InformationCircleIcon,
	MagnifyingGlassIcon,
	PrinterIcon,
	UserIcon,
} from '@heroicons/react/24/outline'
import { ColumnDef } from '@tanstack/react-table'
import {
	ArrowDown,
	ArrowUp,
	ChevronsUpDown,
	CircleChevronLeftIcon,
	CircleChevronRightIcon,
	LogOutIcon,
} from 'lucide-react'
import React, { useMemo, useRef, useState } from 'react'
import Barcode from 'react-barcode'
import { useMutation, useQuery } from 'react-query'
import { useNavigate, useParams } from 'react-router-dom'
import { useReactToPrint } from 'react-to-print'

import { format, parseISO } from 'date-fns'
import { de } from 'date-fns/locale'

import { Button } from '@/components/ui/button'
import { DataTable, DataTableColumnHeader } from '@/components/ui/data-table'
import { Input } from '@/components/ui/input'
import {
	ResponsiveModal,
	ResponsiveModalContent,
	ResponsiveModalDescription,
	ResponsiveModalHeader,
	ResponsiveModalTitle,
} from '@/components/ui/responsive-dialog'
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from '@/components/ui/select'
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from '@/components/ui/table'
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { useAppDispatch, useAppSelector } from '@/hooks'
import { cn } from '@/lib/utils'
import { adminTabs } from '@/pages/admin'
import productService from '@/services/product-service'
import standService from '@/services/stand-service'
import { logout } from '@/slices/auth'
import { useMediaQuery } from 'usehooks-ts'

import { LogoIcon, LogoImage, LogoutIcon, ProfilePhotoImage, TaskSquareIcon } from '@/assets/icons'

const pageStyle = `
  @page {
    size: 210mm 297mm;
    margin: 7% 5%;
  }

  body {
    padding: 0;
		margin: 0;
  }

  @media all {
    .pageBreak {
      display: none;
    }
  }

  @media print {
     .avoid-page-break {
      break-inside: avoid;
      page-break-inside: avoid;
    }

    .pageBreak {
      page-break-before: always;
    }
  }
`

export const StandDetails = () => {
	const { id: standId } = useParams() as { id: string }
	const dispatch = useAppDispatch()
	const navigate = useNavigate()
	const isSmallScreen = useMediaQuery('(max-width: 64rem)')
	const printRef = useRef<HTMLDivElement>(null)

	const auth = useAppSelector(state => state.auth)

	const [isStandDetailDialogVisible, setIsStandDetailDialogVisible] = useState(false)
	const [isBarCodeDialogVisible, setIsBarCodeDialogVisible] = useState<null | string>(null)
	const [expandedRows, setExpandedRows] = useState<number[]>([])
	const [selectedProduct, setSelectedProduct] = useState<Product | null>(null)
	const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
	const [searchValue, setSearchValue] = useState('')
	const [bookingId, setBookingId] = useState<string>('All')
	const [sortColumn, setSortColumn] = useState('')
	const [sortDirection, setSortDirection] = useState('')

	const { data: stand } = useQuery(`fetch-stand-by-${standId}`, () =>
		standService.fetchStandById(Number(standId)),
	)

	const {
		data: products,
		isLoading,
		refetch,
	} = useQuery(`fetch-products-by-${standId}`, () =>
		productService.fetchProductsByStand(Number(standId)),
	)

	const { mutate: updatePrintStatus } = useMutation({
		mutationKey: 'update-print-status',
		mutationFn: (productIds: Product['id'][]) => productService.updatePrintStatus(productIds),
		onSuccess: res => {
			if (res?.data?.status) {
				refetch()
			}
		},
		onError: err => {
			console.warn(err)
		},
	})

	const sortedData = useMemo(() => {
		if (!products) return []
		if (!sortColumn) return products

		return [...products].sort((a, b) => {
			const aValue = a[sortColumn as keyof Product]
			const bValue = b[sortColumn as keyof Product]

			if (aValue == null || bValue == null) return 0

			let aVal: any = aValue
			let bVal: any = bValue

			if (aVal instanceof Date) aVal = aVal.getTime()
			if (bVal instanceof Date) bVal = bVal.getTime()

			const aParsed = typeof aVal === 'string' ? Date.parse(aVal) : NaN
			const bParsed = typeof bVal === 'string' ? Date.parse(bVal) : NaN

			if (!isNaN(aParsed) && !isNaN(bParsed)) {
				aVal = aParsed
				bVal = bParsed
			}

			if (typeof aVal === 'number' && typeof bVal === 'number') {
				return sortDirection === 'asc' ? aVal - bVal : bVal - aVal
			}

			if (typeof aVal === 'string' && typeof bVal === 'string') {
				return sortDirection === 'asc' ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal)
			}

			return 0
		})
	}, [products, sortColumn, sortDirection])

	const { filteredProducts } = useMemo(() => {
		return {
			filteredProducts: products?.filter(curr => {
				if (!searchValue && bookingId === 'All') {
					return true
				}

				const searchLowerCase = searchValue.toLowerCase()
				const status =
					curr?.is_added_to_pos == 1 ? 'Aktiv' : curr?.is_added_to_pos == 2 ? 'Verkauft' : 'Neu'

				const matchesSearch =
					curr.user_id?.toString()?.includes(searchLowerCase) ||
					curr.sku?.toLowerCase()?.includes(searchLowerCase) ||
					curr.title?.toLowerCase()?.includes(searchLowerCase) ||
					curr.manufacturer?.toLowerCase()?.includes(searchLowerCase) ||
					curr.brand_name?.toLowerCase()?.includes(searchLowerCase) ||
					curr.price?.toString()?.includes(searchLowerCase) ||
					curr.quantity?.toString()?.includes(searchLowerCase) ||
					status?.toLowerCase()?.includes(searchLowerCase)

				const matchesBookingId = bookingId === 'All' || curr.booking_id === Number(bookingId)

				return matchesSearch && matchesBookingId
			}),
		}
	}, [sortedData, searchValue, bookingId])

	const newProducts = useMemo(
		() =>
			filteredProducts?.filter(product =>
				bookingId === 'All'
					? product.is_printed === 0
					: product.is_printed === 0 && product.booking_id === Number(bookingId),
			),
		[filteredProducts],
	)

	const handleSort = (column: string) => {
		if (sortColumn === column) {
			setSortDirection(prev => (prev === 'asc' ? 'desc' : 'asc'))
		} else {
			setSortColumn(column)
			setSortDirection('asc')
		}
	}

	const handlePrint = useReactToPrint({
		contentRef: printRef,
		onAfterPrint: () => {
			if (selectedProduct) {
				updatePrintStatus([selectedProduct.id])
			} else {
				const productIds = (isBarCodeDialogVisible === 'new' ? newProducts : filteredProducts)
					?.filter(product => (isBarCodeDialogVisible === 'new' ? !product.is_printed : true))
					.map(product => product.id) as number[]

				updatePrintStatus(productIds)
			}
		},
		pageStyle: pageStyle,
	})

	const toggleRow = (id: number) => {
		setExpandedRows(prev =>
			prev.includes(id) ? prev.filter(rowId => rowId !== id) : [...prev, id],
		)
	}

	const columns: ColumnDef<Product>[] = [
		{
			accessorKey: 'user_id',
			header: ({ column }) => <DataTableColumnHeader column={column} title="User ID" />,
		},
		{
			accessorKey: 'sku',
			header: ({ column }) => <DataTableColumnHeader column={column} title="Produkt-SKU" />,
		},
		{
			accessorKey: 'title',
			header: ({ column }) => <DataTableColumnHeader column={column} title="Produktname" />,
		},
		{
			accessorKey: 'manufacturer',
			header: ({ column }) => <DataTableColumnHeader column={column} title="Herstellername" />,
		},
		{
			accessorKey: 'brand_name',
			header: ({ column }) => <DataTableColumnHeader column={column} title="Markenname" />,
		},
		{
			accessorKey: 'price',
			header: ({ column }) => <DataTableColumnHeader column={column} title="Preis" />,
		},
		{
			accessorKey: 'quantity',
			header: ({ column }) => <DataTableColumnHeader column={column} title="Menge" />,
		},
		{
			accessorKey: 'is_added_to_pos',
			accessorFn: (row: Product) =>
				row.is_added_to_pos === 1 ? 'Aktiv' : row.is_added_to_pos === 2 ? 'Verkauft' : 'Neu',
			header: ({ column }) => <DataTableColumnHeader column={column} title="Status" />,
			cell: ({ row }) => (
				<div
					className={cn(
						'w-fit rounded-[18px] p-1.5 px-3',
						row.getValue('is_added_to_pos') === 'Aktiv' ||
							row.getValue('is_added_to_pos') === 'Verkauft'
							? 'bg-[#61B9641A] text-[#61B964]'
							: 'bg-[#FFEFF2] text-[#DB5358]',
					)}>
					{row.getValue('is_added_to_pos')}
				</div>
			),
		},
		{
			accessorKey: 'inventory_change_timestamps',
			header: ({ column }) => <DataTableColumnHeader column={column} title="Bestandsänderungen" />,
			cell: ({ row }) => {
				if (!row.getValue('inventory_change_timestamps')) return ''
				const formattedDates = (row.getValue('inventory_change_timestamps') as string).split(',')
				const parsedDates = formattedDates.map((date, index) => (
					<div key={index}>
						{index + 1}. {format(parseISO(date), 'dd-MM-yyyy')}
					</div>
				))

				return <div>{parsedDates}</div>
			},
		},
		{
			accessorKey: 'createdAt',
			header: ({ column }) => <DataTableColumnHeader column={column} title="Erstellt" />,
			cell: ({ row }) => <div>{format(new Date(row.getValue('createdAt')), 'dd-MM-yyyy')}</div>,
		},
		{
			id: 'action-stands',
			enableSorting: false,
			header: ({ column }) => <DataTableColumnHeader column={column} title="Aktionen" />,
			cell: ({ row }) => (
				<div className="flex items-center justify-center gap-0.5">
					<div
						onClick={() => setSelectedProduct(row.original)}
						className="border cursor-pointer border-[#0B224D33] hover:border-secondary size-[33px] flex items-center justify-center rounded-full">
						<PrinterIcon className="shrink-0 size-4" />
					</div>
				</div>
			),
		},
	]

	const MobileProductsTable = () => {
		return (
			<Table>
				<TableHeader>
					<TableRow className="bg-[#F7F8F880] border-b-0! py-[13px] px-4 rounded-lg">
						<TableHead
							align="center"
							onClick={() => handleSort('user_id')}
							className="cursor-pointer text-center text-muted-foreground text-[10px] font-medium">
							<div className="flex items-center gap-1 justify-center">
								User ID
								{sortColumn === 'user_id' ? (
									sortDirection === 'desc' ? (
										<ArrowDown size={12} />
									) : (
										<ArrowUp size={12} />
									)
								) : (
									<ChevronsUpDown size={12} />
								)}
							</div>
						</TableHead>
						<TableHead
							align="center"
							onClick={() => handleSort('is_added_to_pos')}
							className="cursor-pointer text-center text-muted-foreground text-[10px] font-medium">
							<div className="flex items-center gap-1 justify-center">
								Status
								{sortColumn === 'is_added_to_pos' ? (
									sortDirection === 'desc' ? (
										<ArrowDown size={12} />
									) : (
										<ArrowUp size={12} />
									)
								) : (
									<ChevronsUpDown size={12} />
								)}
							</div>
						</TableHead>
						<TableHead
							align="center"
							onClick={() => handleSort('createdAt')}
							className="cursor-pointer text-center text-muted-foreground text-[10px] font-medium">
							<div className="flex items-center gap-1 justify-center">
								Datum
								{sortColumn === 'createdAt' ? (
									sortDirection === 'desc' ? (
										<ArrowDown size={12} />
									) : (
										<ArrowUp size={12} />
									)
								) : (
									<ChevronsUpDown size={12} />
								)}
							</div>
						</TableHead>
					</TableRow>
				</TableHeader>
				<TableBody>
					{filteredProducts?.map((product, index) => (
						<React.Fragment key={product.id.toString() + index.toString()}>
							<TableRow
								className={cn(
									'h-14',
									expandedRows.includes(product.id) ? '!border-b-0' : 'border-b border-[#F4F4F4]',
								)}
								onClick={() => toggleRow(product.id)}>
								<TableCell
									align="center"
									className={cn('text-xs', {
										'text-muted-foreground': !expandedRows.includes(product.id),
									})}>
									<div className="flex items-center">
										<ChevronRightIcon
											className={cn(
												'size-3 shrink-0 -mt-0.5',
												expandedRows.includes(product.id)
													? 'rotate-90 text-secondary'
													: 'text-muted-foreground',
											)}
										/>
										{product.user_id}
									</div>
								</TableCell>
								<TableCell
									align="center"
									className={cn('text-[10px]', {
										'text-muted-foreground': !expandedRows.includes(product.id),
									})}>
									<div
										className={cn(
											'text-[10px] w-fit p-1.5 px-2 rounded-[18px]',
											product.is_added_to_pos === 1 || product.is_added_to_pos === 2
												? 'bg-[#61B9641A] text-[#61B964]'
												: 'bg-[#FFEFF2] text-[#DB5358]',
										)}>
										{product.is_added_to_pos === 1
											? 'Aktiv'
											: product.is_added_to_pos === 2
												? 'Verkauft'
												: 'Neu'}
									</div>
								</TableCell>
								<TableCell
									align="center"
									className={cn('text-xs', {
										'text-muted-foreground': !expandedRows.includes(product.id),
									})}>
									{format(new Date(product.createdAt), 'dd-MM-yyyy')}
								</TableCell>
							</TableRow>
							{expandedRows.includes(product.id) && (
								<TableRow
									className={cn(
										'mt-2',
										!expandedRows.includes(product.id)
											? '!border-b-0'
											: 'border-b border-[#F4F4F4]',
									)}>
									<TableCell className="pt-0" colSpan={3}>
										<div className="px-3 flex flex-col gap-4">
											<div className="flex items-center justify-between">
												<h6 className="text-muted-foreground text-[10px] font-medium">
													Produkt-SKU
												</h6>
												<p className="text-secondary text-xs font-medium">{product.sku}</p>
											</div>
											<div className="flex items-center justify-between">
												<h6 className="text-muted-foreground text-[10px] font-medium">
													Produktname
												</h6>
												<p className="text-secondary text-xs font-medium">{product.title}</p>
											</div>
											<div className="flex items-center justify-between">
												<h6 className="text-muted-foreground text-[10px] font-medium">
													Herstellername
												</h6>
												<p className="text-secondary text-xs font-medium">{product.manufacturer}</p>
											</div>
											<div className="flex items-center justify-between">
												<h6 className="text-muted-foreground text-[10px] font-medium">
													Markenname
												</h6>
												<p className="text-secondary text-xs font-medium">{product.brand_name}</p>
											</div>
											<div className="flex items-center justify-between">
												<h6 className="text-muted-foreground text-[10px] font-medium">Preis</h6>
												<p className="text-secondary max-w-[200px] text-right text-xs font-medium">
													{product.price}
												</p>
											</div>
											<div className="flex items-center justify-between">
												<h6 className="text-muted-foreground text-[10px] font-medium">Menge</h6>
												<p className="text-secondary max-w-[200px] text-right text-xs font-medium">
													{product.quantity}
												</p>
											</div>
											<div className="flex items-center justify-between">
												<h6 className="text-muted-foreground text-[10px] font-medium">
													Bestandsänderungen
												</h6>
												<p className="text-secondary max-w-[200px] text-right text-xs font-medium">
													{product.inventory_change_timestamps
														? product.inventory_change_timestamps.split(',').map((date, index) => {
																return (
																	<div key={index}>
																		{index + 1}. {format(parseISO(date), 'dd-MM-yyyy')}
																	</div>
																)
															})
														: ''}
												</p>
											</div>
											<div className="flex items-center justify-between">
												<div className="flex items-center gap-1">
													<InformationCircleIcon className="text-muted-foreground size-3" />
													<h6 className="text-muted-foreground text-[10px] font-medium">Aktion</h6>
												</div>
												<div className="flex items-center gap-1">
													<div
														onClick={() => setSelectedProduct(product)}
														className="border cursor-pointer border-[#0B224D33] hover:border-secondary size-6 flex items-center justify-center rounded-full">
														<PrinterIcon className="shrink-0 max-lg:size-3" />
													</div>
												</div>
											</div>
										</div>
									</TableCell>
								</TableRow>
							)}
						</React.Fragment>
					))}
				</TableBody>
			</Table>
		)
	}

	return (
		<div className="p-5 max-w-[150rem] mx-auto bg-[#F6F6F680] mb-0 flex flex-col gap-2">
			<ResponsiveModal open={!!selectedProduct} onOpenChange={() => setSelectedProduct(null)}>
				<ResponsiveModalContent className="gap-0 !max-w-[730px]">
					<ResponsiveModalHeader className="max-lg:pb-2">
						<ResponsiveModalTitle className="mb-0 max-lg:text-left">
							Tag drucken
						</ResponsiveModalTitle>
						<ResponsiveModalDescription className="sr-only">Tag drucken</ResponsiveModalDescription>
					</ResponsiveModalHeader>
					<div ref={printRef} className="mt-6 lg:mt-8 print:mt-0 print:p-0">
						<div className="grid grid-cols-3 lg:grid-cols-4 print:grid-cols-4 gap-x-[8px] print:gap-x-[8px]">
							<div className="avoid-page-break labelsGrid">
								<div
									style={{
										display: 'flex',
										fontSize: '6px',
										paddingBottom: '2px',
										alignContent: 'space-between',
										alignItems: 'space-between',
									}}>
									<p
										style={{
											marginLeft: '10px',
											paddingTop: '2px',
											fontSize: '8px',
										}}>
										{selectedProduct?.stand_id}
									</p>
									<p
										style={{
											marginLeft: '10px',
											paddingTop: '2px',
											fontSize: '8px',
										}}>
										{selectedProduct?.brand_name && selectedProduct?.brand_name?.length > 13
											? selectedProduct.brand_name.slice(0, 13)
											: selectedProduct?.brand_name}
									</p>
									<p
										style={{
											marginLeft: 'auto',
											marginRight: '5px',
											fontSize: '11px',
											fontWeight: 'bold',
											fontFamily: 'Arial',
										}}>
										{selectedProduct?.price}€
									</p>
								</div>

								<div
									style={{
										display: 'flex',
										fontSize: '7px',
										paddingBottom: '2px',
									}}>
									<p
										style={{
											marginLeft: '10px',
											fontSize: '8px',
											textOverflow: 'ellipsis',
											overflow: 'hidden',
											whiteSpace: 'nowrap',
										}}>
										{selectedProduct?.title}
									</p>
								</div>

								<div
									style={{
										display: 'flex',
										fontSize: '7px',
										paddingBottom: '2px',
									}}>
									<Barcode
										value={selectedProduct?.sku as string}
										marginTop={0}
										height={20}
										font={'sans-serif'}
									/>
								</div>
							</div>
						</div>
					</div>
					<Button
						variant="secondary"
						onClick={() => handlePrint()}
						className="mt-16 w-full lg:h-14 rounded-[12px] font-extrabold text-white">
						Drucken
					</Button>
				</ResponsiveModalContent>
			</ResponsiveModal>
			<ResponsiveModal
				open={!!isBarCodeDialogVisible}
				onOpenChange={() => setIsBarCodeDialogVisible(null)}>
				<ResponsiveModalContent
					crossIconClassName="top-3 lg:top-4.5"
					className="!px-6 !py-4 gap-0 !max-w-[730px]">
					<ResponsiveModalHeader className="max-lg:pb-2">
						<ResponsiveModalTitle className="mb-0 max-lg:text-left">
							{isBarCodeDialogVisible === 'new' ? 'Neue Preisschilder' : 'Preisschilder drucken'}
						</ResponsiveModalTitle>
						<ResponsiveModalDescription className="sr-only">
							{isBarCodeDialogVisible === 'new' ? 'Neue Preisschilder' : 'Preisschilder drucken'}
						</ResponsiveModalDescription>
					</ResponsiveModalHeader>

					<div ref={printRef} className="mt-6 lg:mt-8 print:mt-0 print:p-0">
						<div className="grid grid-cols-3 lg:grid-cols-4 print:grid-cols-4 gap-x-[8px] print:gap-x-[8px]">
							{(isBarCodeDialogVisible === 'new' ? newProducts : filteredProducts)?.flatMap(
								product =>
									Array.from({ length: product?.quantity || 1 }, (_, index) => (
										<div key={`${product.id}-${index}`} className="avoid-page-break labelsGrid">
											<div
												style={{
													display: 'flex',
													fontSize: '6px',
													paddingBottom: '2px',
													alignContent: 'space-between',
													alignItems: 'space-between',
												}}>
												<p
													style={{
														marginLeft: '10px',
														paddingTop: '2px',
														fontSize: '8px',
													}}>
													{product?.stand_id}
												</p>
												<p
													style={{
														marginLeft: '10px',
														paddingTop: '2px',
														fontSize: '8px',
													}}>
													{product?.brand_name?.length > 13
														? product.brand_name.slice(0, 13)
														: product?.brand_name}
												</p>
												<p
													style={{
														marginLeft: 'auto',
														marginRight: '5px',
														fontSize: '11px',
														fontWeight: 'bold',
														fontFamily: 'Arial',
													}}>
													{product?.price}€
												</p>
											</div>

											<div
												style={{
													display: 'flex',
													fontSize: '7px',
													paddingBottom: '2px',
												}}>
												<p
													style={{
														marginLeft: '10px',
														fontSize: '8px',
														textOverflow: 'ellipsis',
														overflow: 'hidden',
														whiteSpace: 'nowrap',
													}}>
													{product?.title}
												</p>
											</div>

											<div
												style={{
													display: 'flex',
													fontSize: '7px',
													paddingBottom: '2px',
												}}>
												<Barcode
													value={product?.sku}
													marginTop={0}
													height={20}
													font={'sans-serif'}
												/>
											</div>
										</div>
									)),
							)}
						</div>
					</div>

					<Button
						onClick={() => handlePrint()}
						className="mt-4 w-full lg:h-14 rounded-[4px] font-extrabold text-white bg-[#d0a667] hover:bg-[#967543]">
						Drucken
					</Button>
				</ResponsiveModalContent>
			</ResponsiveModal>
			<ResponsiveModal
				open={isStandDetailDialogVisible}
				onOpenChange={() => setIsStandDetailDialogVisible(false)}>
				<ResponsiveModalContent className="gap-0 !max-w-[550px]">
					<ResponsiveModalHeader className="max-lg:pb-2">
						<ResponsiveModalTitle className="mb-0 max-lg:text-left">
							Regale Anzeigen
						</ResponsiveModalTitle>
						<ResponsiveModalDescription className="sr-only">
							Regale Anzeigen
						</ResponsiveModalDescription>
					</ResponsiveModalHeader>
					<div className="mt-8 lg:mt-12 flex flex-col gap-4 lg:gap-5">
						<div className="flex items-center justify-between pb-3 border-b border-[#ECECEC]">
							<p className="text-secondary/50 font-medium">Stand Nr.</p>
							<p className="text-secondary font-extrabold">{stand?.label}</p>
						</div>
						<div className="flex items-center justify-between pb-3 border-b border-[#ECECEC]">
							<p className="text-secondary/50 font-medium">Status</p>
							<p
								className={cn(
									'text-secondary text-sm rounded-[18px] h-6 px-[11px] font-medium',
									stand?.status == 'active'
										? 'text-[#61B964] bg-[#61B9641A]'
										: 'bg-[#FFEFF2] text-[#DB5358]',
								)}>
								{stand?.status == 'active' ? 'Aktiv' : 'Deaktiviert'}
							</p>
						</div>
						<div className="flex items-center justify-between pb-3 border-b border-[#ECECEC]">
							<p className="text-secondary/50 font-medium">Verfügbarkeit</p>
							<p className="text-secondary font-extrabold">
								{stand?.is_available ? 'Verfügbar' : 'Gebucht'}
							</p>
						</div>
						<div className="flex items-center justify-between pb-3 border-b border-[#ECECEC]">
							<p className="text-secondary/50 font-medium">Datum</p>
							{stand?.createdAt && (
								<p className="text-secondary font-extrabold">
									{new Date(stand.createdAt).toLocaleDateString()}
								</p>
							)}
						</div>
						<div className="flex items-center justify-between pb-3 border-b border-[#ECECEC]">
							<p className="text-secondary/50 font-medium">Breite</p>
							<p className="text-secondary font-extrabold">{stand?.width}</p>
						</div>
						<div className="flex items-center justify-between pb-3 border-b border-[#ECECEC]">
							<p className="text-secondary/50 font-medium">Höhe</p>
							<p className="text-secondary font-extrabold">{stand?.height}</p>
						</div>
						<div className="flex items-center justify-between pb-3 border-b border-[#ECECEC]">
							<p className="text-secondary/50 font-medium">X</p>
							<p className="text-secondary font-extrabold">{stand?.x}</p>
						</div>
						<div className="flex items-center justify-between pb-3 border-b border-[#ECECEC]">
							<p className="text-secondary/50 font-medium">Y</p>
							<p className="text-secondary font-extrabold">{stand?.y}</p>
						</div>
						<div className="flex items-center justify-between">
							<p className="text-secondary/50 font-medium">Messung</p>
							<p className="text-secondary font-extrabold">{stand?.measurement}</p>
						</div>
					</div>
				</ResponsiveModalContent>
			</ResponsiveModal>
			<Dialog open={mobileMenuOpen} onClose={setMobileMenuOpen} className="lg:hidden">
				<div
					className={cn('fixed inset-0 z-10', {
						'bg-black/20': { mobileMenuOpen },
					})}
				/>
				<DialogPanel className="fixed w-10/12 inset-y-0 left-0 z-10 overflow-y-auto bg-white px-6 py-10 sm:max-w-sm sm:ring-1 sm:ring-gray-900/10">
					<div className="flex items-center justify-between">
						<div className="-m-1.5 p-1.5 flex items-center gap-2">
							<a href="#" className="-m-1.5 p-1.5">
								<span className="sr-only">Book Stands</span>
								<img src={LogoImage} className="h-8 w-auto shrink-0" />
							</a>
							<div className="flex flex-col w-3/5 flex-wrap gap-1">
								<h3 className="text-secondary font-extrabold">minmini</h3>
								<p className="text-[10px] font-medium text-muted-foreground">
									(Ver)kaufe nachhaltig, schaffe Platz und verdiene dabei.
								</p>
							</div>
						</div>
						<button
							type="button"
							onClick={() => setMobileMenuOpen(false)}
							className="-m-2.5 rounded-md p-2.5 text-muted-foreground">
							<span className="sr-only">Close menu</span>
							<XMarkIcon aria-hidden="true" className="size-6" />
						</button>
					</div>
					<div className="mt-3 h-px w-full bg-[#0B224D0D]" />
					<div className="mt-4 flex items-center gap-2">
						<img src={ProfilePhotoImage} className="size-6 rounded-full" />
						<div className="flex flex-col gap-1">
							<h2 className="text-secondary text-xs font-extrabold">
								{auth.first_name} {auth.last_name}
							</h2>
							<p className="text-muted-foreground text-[10px]">{auth.email}</p>
						</div>
					</div>
					<div className="mt-8 flow-root">
						<h4 className="uppercase text-xs text-muted-foreground">Menu</h4>
						<div className="space-y-3 pt-3">
							{adminTabs.map(tab => (
								<div
									key={tab.name}
									onClick={() => {
										navigate(`/admin-panel?tab=${tab.value}`)
										setMobileMenuOpen(false)
									}}
									className={cn(
										'flex items-center cursor-pointer gap-1 text-xs font-medium rounded-lg px-4 py-[13px]',
										'regale' === tab.value ? 'text-white bg-secondary' : 'text-muted-foreground',
									)}>
									<tab.icon />
									{tab.name}
								</div>
							))}
						</div>
						<div
							onClick={() => dispatch(logout())}
							className="flex cursor-pointer items-center mt-10 uppercase gap-1 rounded-lg px-4 py-[13px] text-xs font-medium text-muted-foreground hover:bg-secondary hover:text-white">
							<LogoutIcon />
							Logout
						</div>
					</div>
				</DialogPanel>
			</Dialog>

			<div className="rounded-[20px] bg-white max-lg:p-4 lg:pb-3 lg:pt-6 lg:pl-6 lg:pr-4 flex flex-col gap-[22px]">
				<div className="flex lg:hidden items-center justify-between">
					<div className="flex items-center gap-2">
						<button
							type="button"
							onClick={() => setMobileMenuOpen(true)}
							className="size-8 flex items-center justify-center rounded-md border border-[#EEEFF0] text-secondary">
							<span className="sr-only">Open main menu</span>
							<Bars3Icon aria-hidden="true" className="size-6" />
						</button>
						<h2 className="text-secondary capitalize text-sm font-extrabold">Regale Anzeigen</h2>
					</div>
					<div className="flex items-center gap-2">
						<div
							onClick={() => dispatch(logout())}
							className="border cursor-pointer border-[#EEEFF0] size-8 flex items-center justify-center rounded-xl">
							<LogOutIcon className="size-4 shrink-0 rotate-180" />
						</div>
						<div
							onClick={() => navigate('/admin-panel')}
							className="border cursor-pointer border-[#EEEFF0] size-8 flex items-center justify-center rounded-xl">
							<UserIcon className="size-4 shrink-0" />
						</div>
					</div>
				</div>
				<div className="flex max-lg:hidden items-center justify-between">
					<div className="flex items-center gap-3">
						<LogoIcon onClick={() => dispatch(logout())} className="size-12 shrink-0" />
						<div className="flex flex-col gap-1">
							<h1 className="text-secondary font-extrabold">Admin Dashboard</h1>
							<p className="text-muted-foreground text-sm font-medium">
								{format(new Date(), 'd. MMMM yyyy', { locale: de })}
							</p>
						</div>
					</div>
					<div className="flex items-center gap-5">
						<div className="flex items-center gap-4">
							<div
								onClick={() => dispatch(logout())}
								className="border cursor-pointer border-[#EEEFF0] size-10 flex items-center justify-center rounded-xl">
								<LogOutIcon className="size-5 shrink-0 rotate-180" />
							</div>
							<div
								onClick={() => navigate('/admin-panel')}
								className="border cursor-pointer border-[#EEEFF0] size-10 flex items-center justify-center rounded-xl">
								<UserIcon className="size-5 shrink-0" />
							</div>
							<div className="border cursor-pointer border-[#EEEFF0] size-10 flex items-center justify-center rounded-xl">
								<BellIcon className="size-5 shrink-0" />
							</div>
						</div>
						<div className="flex items-center gap-1">
							<img src={ProfilePhotoImage} className="size-10 rounded-full" />
							<div className="flex flex-col">
								<h2 className="text-secondary text-sm font-extrabold">
									{auth.first_name} {auth.last_name}
								</h2>
								<p className="text-muted-foreground text-xs">{auth.email}</p>
							</div>
						</div>
					</div>
				</div>
				<Tabs
					value=""
					onValueChange={value => navigate(`/admin-panel?tab=${value}`)}
					className="max-lg:hidden">
					<TabsList className="p-1 scrollbar-thin scrollbar-0 scrollbar-track-[#c1c1c1] bg-[#FBFBFB] h-14 overflow-x-auto flex w-full items-stretch justify-between gap-3.5">
						{adminTabs.map(tab => (
							<TabsTrigger
								key={tab.name}
								className="py-2 cursor-pointer px-[22px] data-[state=active]:text-white text-muted-foreground text-sm data-[state=active]:bg-secondary rounded-xl flex items-center gap-1"
								value={tab.value}>
								<tab.icon />
								{tab.name}
							</TabsTrigger>
						))}
					</TabsList>
				</Tabs>
			</div>

			<div className="rounded-[20px] bg-white max-lg:p-4 max-lg:pr-3 lg:px-4 lg:py-6 flex flex-col">
				<div className="flex max-lg:flex-col max-lg:gap-3 lg:items-center justify-between">
					<div className="flex items-center max-lg:justify-between lg:justify-center w-full gap-2">
						<div className="flex items-center gap-2">
							<div className="lg:hidden rounded-xl border border-[#EEEFF0] size-10 flex items-center justify-center">
								<TaskSquareIcon className="text-secondary" />
							</div>
							<div className="flex items-center flex-col justify-center grow gap-1.5">
								<div className="flex items-center gap-2">
									<Button
										disabled={Number(standId) < 2}
										className="size-5 max-lg:hidden"
										variant="ghost"
										onClick={() => navigate(`/stand-details/${Number(standId) - 1}`)}
										size="icon">
										<CircleChevronLeftIcon className="size-5" />
									</Button>
									<h1 className="text-muted-foreground lg:text-secondary text-xs lg:text-2xl font-medium lg:font-extrabold">
										Regale Anzeigen
									</h1>
									<Button
										disabled={Number(standId) > 162}
										className="size-5 max-lg:hidden"
										variant="ghost"
										onClick={() => navigate(`/stand-details/${Number(standId) + 1}`)}
										size="icon">
										<CircleChevronRightIcon className="size-5" />
									</Button>
								</div>
								<p className="font-medium text-secondary/50 max-lg:hidden text-xs">
									Aktueller Stand: {standId}
								</p>
							</div>
						</div>
						<Button
							variant="outline"
							onClick={() => setIsStandDetailDialogVisible(true)}
							className="text-secondary hidden font-normal max-lg:text-xs border-secondary max-lg:h-10 rounded-lg lg:rounded-xl">
							<ClipboardIcon className="size-4 lg:size-6 shrink-0" />
							Regale Anzeigen
						</Button>
					</div>
					<div className="flex flex-col gap-1 lg:hidden">
						<div className="flex items-center gap-2">
							<Button
								disabled={Number(standId) < 2}
								className="size-3.5"
								variant="ghost"
								onClick={() => navigate(`/stand-details/${Number(standId) - 1}`)}
								size="icon">
								<CircleChevronLeftIcon className="size-3.5" />
							</Button>
							<h1 className="text-secondary text-xs lg:text-2xl font-extrabold">Regale Anzeigen</h1>

							<Button
								disabled={Number(standId) > 162}
								className="size-3.5"
								variant="ghost"
								onClick={() => navigate(`/stand-details/${Number(standId) + 1}`)}
								size="icon">
								<CircleChevronRightIcon className="size-3.5" />
							</Button>
						</div>
						<p className="font-medium text-secondary/50 text-xs">Aktueller Stand: {standId}</p>
					</div>
					<Button
						variant="outline"
						onClick={() => setIsStandDetailDialogVisible(true)}
						className="text-secondary hidden font-normal max-lg:text-xs border-secondary max-lg:h-10 rounded-lg lg:rounded-xl">
						<ClipboardIcon className="size-4 lg:size-6 shrink-0" />
						Regale Anzeigen
					</Button>
				</div>
				<div className="flex items-center max-lg:flex-col max-lg:gap-3 lg:justify-between mt-4 lg:mt-10">
					<div className="flex max-lg:w-full items-center gap-2 lg:gap-5">
						<Button
							variant="outline"
							onClick={() => setIsBarCodeDialogVisible('all')}
							className="text-secondary max-lg:flex-1 font-normal max-lg:text-xs border-secondary max-lg:h-10 rounded-lg lg:rounded-xl">
							<PrinterIcon className="max-lg:hidden lg:size-6 shrink-0" />
							Alle Etiketten drucken
						</Button>
						<Button
							variant="outline"
							onClick={() => setIsBarCodeDialogVisible('new')}
							className="text-secondary max-lg:flex-1 font-normal max-lg:text-xs border-secondary max-lg:h-10 rounded-lg lg:rounded-xl">
							<PrinterIcon className="max-lg:hidden lg:size-6 shrink-0" />
							Neue Etiketten drucken
						</Button>
					</div>
					<div className="flex max-lg:w-full items-center max-lg:flex-row-reverse gap-2 lg:gap-3">
						<div className="relative max-lg:flex-1 flex items-center">
							<Input
								value={searchValue}
								onChange={e => setSearchValue(e.target.value)}
								className="!pl-[34px] h-10 text-xs"
								placeholder="Suche..."
							/>
							<MagnifyingGlassIcon className="size-4 text-muted-foreground absolute left-2" />
						</div>
						<Select value={bookingId} onValueChange={value => setBookingId(value)}>
							<SelectTrigger className="h-10 lg:h-12 lg:w-[169px] max-lg:flex-1 max-lg:w-full text-xs">
								<SelectValue placeholder="Alle Buchungen" />
							</SelectTrigger>
							<SelectContent>
								<SelectItem value="All">Alle Buchungen</SelectItem>
								{Array.from(new Set(products?.map(product => product.booking_id))).map(
									bookingId => (
										<SelectItem key={bookingId} value={bookingId.toString()}>
											{bookingId}
										</SelectItem>
									),
								)}
							</SelectContent>
						</Select>
					</div>
				</div>
				<div className="mt-4">
					{isSmallScreen ? (
						<MobileProductsTable />
					) : (
						<DataTable columns={columns} data={filteredProducts ?? []} isLoading={isLoading} />
					)}
				</div>
			</div>
		</div>
	)
}
